import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { LaunchPadImpactStats } from "@/api/launchPad";

type LaunchPadHeroProps = {
  userEmail: string;
  municipalityName?: string;
  impactStats?: LaunchPadImpactStats;
  impactLoading: boolean;
};

export function LaunchPadHero({
  userEmail,
  municipalityName,
  impactStats,
  impactLoading,
}: LaunchPadHeroProps) {
  const firstName = userEmail.split("@")[0]?.split(".")[0];
  const greetingName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : "there";

  return (
    <div className="space-y-4" data-testid="launch-pad-hero">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          Welcome back, {greetingName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          {municipalityName
            ? `Your home base for ${municipalityName}.`
            : "Select a municipality to see your impact and recent work."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ImpactStatCard
          label="Your time saved with Ordinal"
          description="Estimated from your Research Assistant conversations"
          value={impactStats?.time_saved.display}
          loading={impactLoading}
        />
        <ImpactStatCard
          label="Resident questions answered"
          description={
            municipalityName
              ? `Via Ordinal Connect for ${municipalityName}`
              : "Via Ordinal Connect across your municipalities"
          }
          value={
            impactStats != null
              ? impactStats.constituent_questions_answered.toLocaleString()
              : undefined
          }
          loading={impactLoading}
        />
      </div>
    </div>
  );
}

function ImpactStatCard({
  label,
  description,
  value,
  loading,
}: {
  label: string;
  description: string;
  value?: string;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-1 pt-4">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="pb-4 pt-0">
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <p className="text-2xl font-semibold tabular-nums tracking-tight">{value ?? "—"}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}
